<template>
  <div>
    <template v-if="canWebviewEdit">
      <b-btn
        variant="primary"
        @click="onAddOverride">
        Add New Override
      </b-btn>
      <div class="text-muted my-2">
        Click on an existing override to modify or remove.
      </div>
    </template>
    <v-calendar
      :attributes="attributes"
      :columns="layout.columns"
      :rows="layout.rows"
      :is-expanded="layout.isExpanded">
      <template v-slot:day-popover="{attributes}">
        <div class="h6 mb-0">
          <template v-if="attributes[0].customData.enabled">
            <b-icon-check-circle-fill
              variant="success"
              class="mr-1" />
            Override is enabled
          </template>
          <template v-else>
            <b-icon-exclamation-circle-fill
              variant="secondary"
              class="mr-1" />
            Override is disabled
          </template>
        </div>
        <div v-if="attributes[0].customData.urls.en">
          <strong>English URL:</strong>
          {{ attributes[0].customData.urls.en }}
        </div>
        <div v-if="attributes[0].customData.urls.es">
          <strong>Spanish URL:</strong>
          {{ attributes[0].customData.urls.es }}
        </div>
        <div v-if="attributes[0].customData.urls.ja">
          <strong>Japanese URL:</strong>
          {{ attributes[0].customData.urls.ja }}
        </div>
        <div class="d-flex justify-content-between">
          <b-btn
            variant="link"
            size="sm"
            @click.prevent="onEditOverride(attributes[0].key)">
            {{ canWebviewEdit ? 'Edit' : 'View' }}
          </b-btn>
          <b-btn
            v-if="canWebviewEdit"
            variant="link"
            size="sm"
            @click.prevent="onDeleteOverride(attributes[0].key)">
            Remove
          </b-btn>
        </div>
      </template>
    </v-calendar>
    <b-modal
      v-model="showOverrideModal"
      size="lg"
      hide-header
      hide-footer
      no-close-on-backdrop
      no-close-on-esc>
      <template v-if="editedAttributeKey">
        <h3>{{ canWebviewEdit ? 'Edit' : 'View' }} Web View Override</h3>
        <web-view-override-form
          :config="editedOverride"
          :start-date="editedOverrideStartDate"
          :end-date="editedOverrideEndDate"
          :disabled-dates="disallowedDatesForEdit"
          @save="onSaveOverride"
          @cancel="onCancelEdit" />
      </template>
      <template v-else>
        <h3>Create Web View Override</h3>
        <web-view-override-form
          :disabled-dates="disallowedDatesForEdit"
          @save="onSaveOverride"
          @cancel="onCancelEdit" />
      </template>
    </b-modal>
  </div>
</template>

<script>
  import VCalendar from 'v-calendar/lib/components/calendar.umd'
  import {DateTime} from "luxon"
  import uuid from "uuid"
  import WebViewOverrideForm from "@/components/WebView/WebViewOverrideForm"
  import webview from "@/services/webview"
  import _ from "lodash"
  import {mapGetters} from "vuex"

  export default {
    name: "WebViewOverrideSelection",
    components: {
      WebViewOverrideForm,
      VCalendar,
    },
    props: {
      value: {
        type: Array,
        default: () => [],
      },
    },
    data: () => ({
      attributes: [],
      showOverrideModal: false,
      editedAttributeKey: null,
      editedOverrideStartDate: null,
      editedOverrideEndDate: null,
      editedOverride: null,
    }),
    computed: {
      ...mapGetters([
        "canWebviewEdit",
      ]),
      layout() {
        return this.$screens(
          {
            default: {
              columns: 1,
              rows: 1,
              isExpanded: true,
            },
            md: {
              columns: 2,
              rows: 2,
              isExpanded: true,
            },
            lg: {
              columns: 3,
              rows: 2,
              isExpanded: true,
            },
          },
        )
      },
      disallowedDatesForEdit() {
        return this.attributes
          .filter(attr => attr.key !== this.editedAttributeKey)
          .map(attr => attr.dates)
      },
    },
    watch: {
      value: {
        deep: true,
        handler() {
          this.initCalendarState()
        },
      },
      showOverrideModal(newVal) {
        if (!newVal) {
          this.editedAttributeKey = null
          this.editedOverrideStartDate = null
          this.editedOverrideEndDate = null
          this.editedOverride = null
        }
      },
    },
    created() {
      this.initCalendarState()
    },
    methods: {
      initCalendarState() {
        const attrs = []

        const overrides = _.cloneDeep(this.value)
          .sort((a, b) => a.date.localeCompare(b.date))
        for (let i = 0; i < overrides.length;) {
          const override = overrides[i]
          const dates = [DateTime.fromISO(override.date)]
          // Check for identical overrides over consecutive dates, and group them together
          for (let j = i + 1; j < overrides.length; j++) {
            const nextOverride = overrides[j]
            const nextDate = DateTime.fromISO(nextOverride.date)
            if (nextDate.minus({days: 1}).equals(dates[dates.length - 1])
              && webview.configsAreEqual(override, nextOverride)) {
              dates.push(nextDate)
            } else {
              break
            }
          }

          attrs.push(this.createCalendarAttr(dates[0].toJSDate(), dates[dates.length - 1].toJSDate(), override))
          i += dates.length
        }

        this.attributes = attrs
      },
      emitCurrentState() {
        // Ungroup override ranges into individual dates.
        const updatedOverrides = this.attributes.flatMap(attr => {
          const overrides = []
          const {dates, customData} = attr

          let currentDate = DateTime.fromJSDate(dates.start)
          const endDate = DateTime.fromJSDate(dates.end)
          while (currentDate <= endDate) {
            overrides.push({
              date: currentDate.toISODate(),
              ...customData,
            })
            currentDate = currentDate.plus({days: 1})
          }

          return overrides
        }).sort((a, b) => a.date.localeCompare(b.date))
        this.$emit("input", updatedOverrides)
      },
      onSaveOverride(startDate, endDate, config) {
        if (!this.canWebviewEdit) {
          return
        }

        if (this.editedAttributeKey) {
          const attr = this.attributes.find(it => it.key === this.editedAttributeKey)
          attr.dates = {
            start: startDate,
            end: endDate,
          }
          attr.customData = _.cloneDeep(config)
        } else {
          this.attributes.push(this.createCalendarAttr(startDate, endDate, config))
        }

        this.emitCurrentState()
        this.showOverrideModal = false
      },
      onEditOverride(attrId) {
        const attr = this.attributes.find(it => it.key === attrId)
        if (!attr) {
          return
        }

        this.editedAttributeKey = attrId
        this.editedOverrideStartDate = attr.dates.start
        this.editedOverrideEndDate = attr.dates.end
        this.editedOverride = attr.customData

        this.showOverrideModal = true
      },
      onAddOverride() {
        this.showOverrideModal = true
      },
      async onDeleteOverride(attrId) {
        if (!this.canWebviewEdit) {
          return
        }

        const modalConfirmed = await this.$confirm.open("Are you sure you wish to remove this Web View override?")
        if (!modalConfirmed) {
          return
        }

        this.attributes = [...this.attributes].filter(it => it.key !== attrId)
        this.emitCurrentState()
      },
      onCancelEdit() {
        this.showOverrideModal = false
      },
      createCalendarAttr(startDate, endDate, webviewConfig) {
        const config = _.cloneDeep(webviewConfig)
        delete config.date
        return {
          key: uuid.v4(),
          highlight: webviewConfig.enabled ? true : "gray",
          dates: {
            start: startDate,
            end: endDate,
          },
          popover: {
            isInteractive: true,
            visibility: "click",
          },
          customData: config,
        }
      },
    },
  }
</script>

<style scoped>

</style>
